import { CARD_H, CARD_W, ends } from "@/lib/family";

const key = (a, b) => [a, b].sort().join("|");
const STROKE = "rgb(var(--slate-500))";

export const FamilyLines = ({ fam, pos, hi }) => {
  const has = (id) => pos.has(id);
  const cx = (id) => pos.get(id).x + CARD_W / 2, top = (id) => pos.get(id).y, bot = (id) => top(id) + CARD_H, mid = (id) => top(id) + CARD_H / 2;
  const coupleSet = new Set(fam.couples.map((c) => key(c.a, c.b)));
  const parentsOf = new Map();
  fam.edges.filter((e) => e.kind === "pc").forEach((e) => parentsOf.set(e.down, [...(parentsOf.get(e.down) || []), e]));
  const out = [], placed = [];
  const add = (d, ids, style = {}, label) => {
    if (label) {
      while (placed.some((p) => Math.abs(p.x - label.x) < 60 && Math.abs(p.y - label.y) < 13)) label = { ...label, y: label.y + 13 };
      placed.push(label);
    }
    out.push({ d, ids, style, label });
  };
  const between = (a, b) => {
    if (top(a) === top(b)) {
      const [l, r] = pos.get(a).x < pos.get(b).x ? [a, b] : [b, a];
      return { d: `M${pos.get(l).x + CARD_W} ${mid(l)} L${pos.get(r).x} ${mid(r)}`, lx: (pos.get(l).x + CARD_W + pos.get(r).x) / 2, ly: mid(l) - 6 };
    }
    const [u, w] = top(a) < top(b) ? [a, b] : [b, a];
    return { d: `M${cx(u)} ${bot(u)} L${cx(w)} ${top(w)}`, lx: (cx(u) + cx(w)) / 2, ly: (bot(u) + top(w)) / 2 };
  };
  fam.couples.filter((c) => has(c.a) && has(c.b)).forEach((c) => { const b = between(c.a, c.b); add(b.d, [c.a, c.b], { stroke: "#f59e0b", strokeWidth: 2.5 }); });
  parentsOf.forEach((es, child) => {
    if (!has(child)) return;
    const ps = es.filter((e) => has(e.up));
    const ym = top(child) - 30;
    const done = new Set();
    for (let i = 0; i < ps.length; i++) for (let j = i + 1; j < ps.length; j++) {
      const [a, b] = [ps[i].up, ps[j].up];
      if (done.has(a) || done.has(b) || !coupleSet.has(key(a, b)) || top(a) !== top(b)) continue;
      const mx = (cx(a) + cx(b)) / 2;
      add(`M${mx} ${mid(a)} L${mx} ${ym} L${cx(child)} ${ym} L${cx(child)} ${top(child)}`, [a, b, child], ps[i].adoptive || ps[j].adoptive ? { strokeDasharray: "6 5" } : {});
      done.add(a); done.add(b);
    }
    ps.filter((e) => !done.has(e.up)).forEach((e) =>
      add(`M${cx(e.up)} ${bot(e.up)} L${cx(e.up)} ${ym} L${cx(child)} ${ym} L${cx(child)} ${top(child)}`, [e.up, child], e.adoptive ? { strokeDasharray: "6 5" } : {}));
  });
  fam.edges.filter((e) => e.kind === "sib" && has(e.a) && has(e.b)).forEach((e) => {
    const pa = (parentsOf.get(e.a) || []).map((x) => x.up), pb = (parentsOf.get(e.b) || []).map((x) => x.up);
    if (pa.some((p) => pb.includes(p) && has(p))) return;
    const y0 = Math.min(top(e.a), top(e.b)) - 16;
    add(`M${cx(e.a)} ${top(e.a)} L${cx(e.a)} ${y0} L${cx(e.b)} ${y0} L${cx(e.b)} ${top(e.b)}`, [e.a, e.b], e.half ? { strokeDasharray: "6 5" } : {},
      e.half ? { x: (cx(e.a) + cx(e.b)) / 2, y: y0 - 5, t: "half" } : null);
  });
  fam.edges.filter((e) => (e.kind === "ext" || e.kind === "other") && ends(e).every(has)).forEach((e) => {
    const [a, b] = e.up ? [e.up, e.down] : [e.a, e.b];
    let l = between(a, b);
    if (top(a) !== top(b)) {
      // Bow around the right side so the line doesn't run through cards in between
      const [u, w] = top(a) < top(b) ? [a, b] : [b, a];
      const ux = pos.get(u).x + CARD_W, wx = pos.get(w).x + CARD_W;
      const span = [...pos.values()].filter((p) => p.y >= top(u) && p.y <= top(w)).map((p) => p.x + CARD_W);
      const bow = Math.max(...span) + 40;
      l = { d: `M${ux} ${mid(u)} L${bow} ${mid(u)} L${bow} ${mid(w)} L${wx} ${mid(w)}`, lx: bow + 32, ly: (mid(u) + mid(w)) / 2 };
    }
    add(l.d, [a, b], { strokeDasharray: "2 5", strokeWidth: e.kind === "other" ? 1 : 1.5 }, { x: l.lx, y: l.ly, t: e.label });
  });
  return (
    <g data-testid="family-lines">
      {out.map((l, i) => {
        const on = !hi || l.ids.includes(hi);
        return (
          <g key={i} opacity={on ? 1 : 0.12} className="transition-opacity duration-300">
            <path d={l.d} fill="none" strokeWidth={1.5} {...l.style} style={{ stroke: l.style?.stroke || STROKE }} />
            {l.label && <text x={l.label.x} y={l.label.y} textAnchor="middle" fontSize="10" style={{ fill: "rgb(var(--slate-400))" }} fontFamily="JetBrains Mono">{l.label.t}</text>}
          </g>
        );
      })}
    </g>
  );
};

